/**
 * @file AdminDashboard.jsx
 * @description Task Management System Admin Dashboard page with rich stats, non-closed task tables, filters, and multi-sort features.
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { listTasks, getAllLabels } from "../../firebase/tasks";
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../../firebase/config";
import TaskSearchInput from "../../components/tms/TaskSearchInput";
import { LabelTag } from "../../components/tms/LabelTag";
import { describeRecurrence, isRepeatableTask, isActiveRecurringTask } from "../../utils/recurrenceEngine";

// Priority weight for sorting
const priorityWeight = {
  High: 3,
  Medium: 2,
  Low: 1
};

// Helper for Priority Badges
const getPriorityBadgeClass = (priority) => {
  switch (priority) {
    case "Critical": // keeping for legacy
    case "High":
      return "bg-rose-100 text-rose-700 border border-rose-200 px-2.5 py-0.5 rounded-full text-xs font-bold tracking-wide inline-block";
    case "Medium":
      return "bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "Low":
    default:
      return "bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
  }
};

// Helper for Status Badges
const getStatusBadgeClass = (status) => {
  switch (status) {
    case "Open":
      return "bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "In Progress":
      return "bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "On Hold":
      return "bg-amber-100 text-amber-800 border border-amber-200 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
    case "Closed":
    default:
      return "bg-slate-100 text-slate-700 border border-slate-300 px-2.5 py-0.5 rounded-full text-xs font-semibold tracking-wide inline-block";
  }
};

const formatFirestoreDate = (timestamp) => {
  if (!timestamp) return "—";
  if (timestamp.toDate && typeof timestamp.toDate === "function") {
    return timestamp.toDate().toLocaleDateString();
  }
  return new Date(timestamp).toLocaleDateString();
};

export default function AdminDashboard() {
  const [allTasks, setAllTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [selectedState, setSelectedState] = useState("");
  const [selectedPriority, setSelectedPriority] = useState("");
  const [selectedAssignee, setSelectedAssignee] = useState("");
  const [selectedLabel, setSelectedLabel] = useState("");
  const [selectedRecurrence, setSelectedRecurrence] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Sort States
  const [sortBy, setSortBy] = useState("taskNumber");
  const [sortOrder, setSortOrder] = useState("desc");

  // Fetch Users & Tasks
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch representative users
        const usersRef = collection(firestore, "users");
        const usersSnap = await getDocs(usersRef);
        const usersList = usersSnap.docs.map((docSnap) => ({
          uid: docSnap.id,
          name: docSnap.data().name || docSnap.data().email || docSnap.id
        }));
        setUsers(usersList);

        const labelsList = await getAllLabels();
        setLabels(labelsList);

        // Fetch all tasks (then filter/process client-side for absolute reliability)
        const tasksList = await listTasks();
        setAllTasks(tasksList);
      } catch (err) {
        console.error("Error loading admin dashboard data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getAssigneeName = (uid) => {
    if (!uid) return "Unassigned";
    const matched = users.find((u) => u.uid === uid);
    return matched ? matched.name : "Unknown User";
  };

  // Calculate Stat Cards (counts for all active non-closed tasks)
  const stats = {
    totalOpen: 0,
    inProgress: 0,
    onHold: 0,
    recurring: 0,
    high: 0,
    medium: 0,
    low: 0
  };
  const activeLabels = new Set();

  allTasks.forEach((task) => {
    if (task.status !== "Closed") {
      if (task.status === "Open") stats.totalOpen++;
      if (task.status === "In Progress") stats.inProgress++;
      if (task.status === "On Hold") stats.onHold++;
      if (isActiveRecurringTask(task)) stats.recurring++;

      const p = task.priority || "Low";
      if (p === "High" || p === "Critical") stats.high++;
      if (p === "Medium") stats.medium++;
      if (p === "Low") stats.low++;

      if (Array.isArray(task.labels)) {
        task.labels.forEach((l) => activeLabels.add(l));
      }
    }
  });
  const labelsInUseCount = activeLabels.size;

  // Client-side Filtering
  const filteredTasks = allTasks.filter((task) => {
    // 1. Must be non-closed
    if (task.status === "Closed") return false;

    // 2. State Filter
    if (selectedState && task.status !== selectedState) return false;

    // 3. Priority Filter
    if (selectedPriority && task.priority !== selectedPriority) return false;

    // 4. Assignee Filter
    if (selectedAssignee && task.assigneeUid !== selectedAssignee) return false;

    // 5. Label Filter
    if (selectedLabel && !(task.labels || []).includes(selectedLabel)) return false;

    // 6. Recurrence Filter
    if (selectedRecurrence === "recurring" && !isRepeatableTask(task)) return false;
    if (selectedRecurrence === "non_recurring" && isRepeatableTask(task)) return false;

    // 7. Date Range Filter (based on createdAt timestamp)
    if (startDate) {
      const taskDate = task.createdAt?.toDate ? task.createdAt.toDate() : new Date(task.createdAt);
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      if (taskDate < start) return false;
    }
    if (endDate) {
      const taskDate = task.createdAt?.toDate ? task.createdAt.toDate() : new Date(task.createdAt);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      if (taskDate > end) return false;
    }

    return true;
  });

  const labelColorMap = {};
  labels.forEach(l => {
    labelColorMap[l.name] = l.color;
  });

  // Client-side Sorting
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    let valA, valB;

    if (sortBy === "taskNumber") {
      valA = a.taskNumber || 0;
      valB = b.taskNumber || 0;
    } else if (sortBy === "dueDate") {
      valA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
      valB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
    } else if (sortBy === "createdAt") {
      valA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
      valB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
    } else if (sortBy === "priority") {
      valA = priorityWeight[a.priority] || 0;
      valB = priorityWeight[b.priority] || 0;
    }

    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const handleSortToggle = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc"); // Default to descending
    }
  };

  const renderSortIndicator = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === "asc" ? " ▴" : " ▾";
  };

  const inputStyle = {
    backgroundColor: "var(--theme-input-bg)",
    border: "1px solid var(--theme-input-border)",
    borderRadius: "var(--theme-input-radius)",
    color: "var(--theme-color-ink)",
  };

  const statCards = [
    {
      label: "Total Open",
      value: stats.totalOpen,
      iconColor: "var(--theme-color-primary)",
      iconBg: "var(--theme-color-canvas-soft)",
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      ),
    },
    {
      label: "In Progress",
      value: stats.inProgress,
      iconColor: "#9f7aea",
      iconBg: "#faf5ff",
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.2"
        />
      ),
    },
    {
      label: "On Hold",
      value: stats.onHold,
      iconColor: "#d97706",
      iconBg: "#fffbeb",
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
          d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      ),
    },
    {
      label: "Repeatable",
      value: stats.recurring,
      iconColor: "#6366f1",
      iconBg: "#eef2ff",
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
          d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.2"
        />
      ),
    },
    {
      label: "Labels in Use",
      value: labelsInUseCount,
      iconColor: "#ec4899",
      iconBg: "#fdf2f8",
      icon: (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
          d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
        />
      ),
    },
  ];

  return (
    <div
      className="container-fluid min-h-screen py-8 px-4 md:px-8"
      style={{ backgroundColor: "var(--theme-color-canvas)" }}
    >
      {/* Header */}
      <div
        className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 pb-5"
        style={{ borderBottom: "1px solid var(--theme-color-mute)" }}
      >
        <div>
          <div className="flex flex-wrap items-center gap-4">
            <h1
              className="text-3xl font-extrabold tracking-tight m-0"
              style={{ color: "var(--theme-color-ink)", fontFamily: "var(--theme-font-family-display)" }}
            >
              TMS Administrative Dashboard
            </h1>
            <TaskSearchInput />
          </div>
          <p className="mt-1 text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
            Unified management console for tracking system-wide active tasks and metrics.
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex gap-3">
          <Link
            to="/tasks"
            className="inline-flex items-center justify-center font-semibold py-2 px-4 rounded-lg border transition-colors text-sm no-underline"
            style={{
              backgroundColor: "var(--theme-color-canvas-soft)",
              color: "var(--theme-color-body)",
              borderColor: "var(--theme-color-mute)",
            }}
          >
            ← Standard View
          </Link>
          <Link
            to="/tasks/new"
            className="inline-flex items-center justify-center font-bold py-2 px-4 rounded-lg shadow-sm hover:shadow transition-colors text-sm no-underline"
            style={{
              backgroundColor: "var(--theme-button-primary-bg)",
              color: "var(--theme-button-primary-text)",
              borderRadius: "var(--theme-button-primary-radius)",
            }}
          >
            + Create Task
          </Link>
        </div>
      </div>

      {loading ? (
        /* Top stat card skeletons */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8 animate-pulse">
          {[...Array(5)].map((_, idx) => (
            <div
              key={idx}
              className="rounded-2xl p-6 border h-32"
              style={{
                backgroundColor: "var(--theme-card-bg)",
                borderColor: "var(--theme-color-mute)",
              }}
            >
              <div className="h-4 bg-slate-200 rounded w-24 mb-4"></div>
              <div className="h-8 bg-slate-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            {statCards.map((card) => (
              <div
                key={card.label}
                className="rounded-2xl border shadow-sm p-6 flex items-center justify-between"
                style={{
                  backgroundColor: "var(--theme-card-bg)",
                  borderColor: "var(--theme-color-mute)",
                  borderRadius: "var(--theme-card-radius)",
                  borderTop: "4px solid var(--theme-color-primary)",
                }}
              >
                <div>
                  <span
                    className="text-sm font-bold uppercase tracking-wider block mb-1"
                    style={{ color: "var(--theme-color-body-mid)" }}
                  >
                    {card.label}
                  </span>
                  <span
                    className="text-3xl font-extrabold"
                    style={{ color: "var(--theme-color-ink)", fontFamily: "var(--theme-font-family-display)" }}
                  >
                    {card.value}
                  </span>
                </div>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: card.iconBg, color: card.iconColor }}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {card.icon}
                  </svg>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Card 4: Priority Metrics */}
            <div
              className="rounded-2xl border shadow-sm p-5 flex flex-col justify-between"
              style={{
                backgroundColor: "var(--theme-card-bg)",
                borderColor: "var(--theme-color-mute)",
                borderRadius: "var(--theme-card-radius)",
                borderTop: "4px solid var(--theme-color-primary)",
              }}
            >
              <span
                className="text-xs font-bold uppercase tracking-wider block mb-2 pb-1.5"
                style={{
                  color: "var(--theme-color-body-mid)",
                  borderBottom: "1px solid var(--theme-color-canvas-soft)",
                }}
              >
                Active Priorities (Non-Closed)
              </span>
              <div className="grid grid-cols-3 gap-x-4 gap-y-1.5 text-xs font-bold" style={{ color: "var(--theme-color-ink)" }}>
                <div className="flex justify-between items-center bg-red-50 px-2 py-1 rounded">
                  <span>🎒 High</span>
                  <span className="text-red-700">{stats.high}</span>
                </div>
                <div className="flex justify-between items-center bg-amber-50 px-2 py-1 rounded">
                  <span>🟡 Med</span>
                  <span className="text-amber-700">{stats.medium}</span>
                </div>
                <div className="flex justify-between items-center bg-emerald-50 px-2 py-1 rounded">
                  <span>🟢 Low</span>
                  <span className="text-emerald-700">{stats.low}</span>
                </div>
              </div>
            </div>

            {/* Card 5: Administrative Reports Quick Links */}
            <div
              className="rounded-2xl border shadow-sm p-5 flex flex-col justify-between"
              style={{
                backgroundColor: "var(--theme-card-bg)",
                borderColor: "var(--theme-color-mute)",
                borderRadius: "var(--theme-card-radius)",
                borderTop: "4px solid var(--theme-color-primary)",
              }}
            >
              <span
                className="text-xs font-bold uppercase tracking-wider block mb-2 pb-1.5"
                style={{
                  color: "var(--theme-color-body-mid)",
                  borderBottom: "1px solid var(--theme-color-canvas-soft)",
                }}
              >
                TMS Reports &amp; Analytics
              </span>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs font-bold pt-1">
                <Link
                  to="/tasks/reports/by-user"
                  className="flex flex-col justify-center items-center p-3 rounded border hover:bg-slate-50 transition-colors text-center no-underline"
                  style={{
                    backgroundColor: "var(--theme-color-canvas-soft)",
                    borderColor: "var(--theme-color-mute)",
                    color: "var(--theme-color-ink)",
                  }}
                >
                  <span className="text-lg mb-1">👤</span>
                  <span>Tasks by User</span>
                </Link>
                <Link
                  to="/tasks/reports/by-state"
                  className="flex flex-col justify-center items-center p-3 rounded border hover:bg-slate-50 transition-colors text-center no-underline"
                  style={{
                    backgroundColor: "var(--theme-color-canvas-soft)",
                    borderColor: "var(--theme-color-mute)",
                    color: "var(--theme-color-ink)",
                  }}
                >
                  <span className="text-lg mb-1">📊</span>
                  <span>Tasks by State</span>
                </Link>
                <Link
                  to="/tasks/reports/by-label"
                  className="flex flex-col justify-center items-center p-3 rounded border hover:bg-slate-50 border-pink-200 transition-colors text-center no-underline"
                  style={{
                    backgroundColor: "var(--theme-color-canvas-soft)",
                    borderColor: "var(--theme-color-mute)",
                    color: "var(--theme-color-ink)",
                  }}
                >
                  <span className="text-lg mb-1">🏷️</span>
                  <span>Tasks by Label</span>
                </Link>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Filter Options */}
      <div
        className="rounded-2xl border shadow-sm p-6 mb-8"
        style={{
          backgroundColor: "var(--theme-card-bg)",
          borderColor: "var(--theme-color-mute)",
          borderRadius: "var(--theme-card-radius)",
        }}
      >
        <div
          className="flex items-center justify-between mb-4 pb-3"
          style={{ borderBottom: "1px solid var(--theme-color-mute)" }}
        >
          <h2
            className="text-xs font-extrabold uppercase tracking-widest m-0"
            style={{ color: "var(--theme-color-body-mid)" }}
          >
            Advanced Filters &amp; Search Parameters
          </h2>
          {(selectedState || selectedPriority || selectedAssignee || selectedLabel || selectedRecurrence || startDate || endDate) && (
            <button
              onClick={() => {
                setSelectedState("");
                setSelectedPriority("");
                setSelectedAssignee("");
                setSelectedLabel("");
                setSelectedRecurrence("");
                setStartDate("");
                setEndDate("");
              }}
              className="text-xs font-bold hover:underline border-0 bg-transparent cursor-pointer"
              style={{ color: "var(--theme-color-primary)" }}
            >
              Clear All Filters
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
          {/* Dropdown 1: State */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--theme-color-body-mid)" }}>
              Task State
            </label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="w-full px-3 py-2 font-medium text-xs focus:outline-none focus:ring-2 transition-all"
              style={inputStyle}
            >
              <option value="">All Active States</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="On Hold">On Hold</option>
            </select>
          </div>

          {/* Dropdown 2: Priority */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--theme-color-body-mid)" }}>
              Priority
            </label>
            <select
              value={selectedPriority}
              onChange={(e) => setSelectedPriority(e.target.value)}
              className="w-full px-3 py-2 font-medium text-xs focus:outline-none focus:ring-2 transition-all"
              style={inputStyle}
            >
              <option value="">All Priorities</option>
              <option value="High">🎒 High</option>
              <option value="Medium">🟡 Medium</option>
              <option value="Low">🟢 Low</option>
            </select>
          </div>

          {/* Dropdown 3: Assignee */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--theme-color-body-mid)" }}>
              Assignee
            </label>
            <select
              value={selectedAssignee}
              onChange={(e) => setSelectedAssignee(e.target.value)}
              className="w-full px-3 py-2 font-medium text-xs focus:outline-none focus:ring-2 transition-all"
              style={inputStyle}
            >
              <option value="">All Representatives</option>
              {users.map((u) => (
                <option key={u.uid} value={u.uid}>{u.name}</option>
              ))}
            </select>
          </div>

          {/* Dropdown 4: Label */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--theme-color-body-mid)" }}>
              Label
            </label>
            <select
              value={selectedLabel}
              onChange={(e) => setSelectedLabel(e.target.value)}
              className="w-full px-3 py-2 font-medium text-xs focus:outline-none focus:ring-2 transition-all"
              style={inputStyle}
            >
              <option value="">All Labels</option>
              {labels.map((l) => (
                <option key={l.id} value={l.name}>🏷️ {l.name}</option>
              ))}
            </select>
          </div>

          {/* Dropdown 5: Recurrence */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--theme-color-body-mid)" }}>
              Recurrence
            </label>
            <select
              value={selectedRecurrence}
              onChange={(e) => setSelectedRecurrence(e.target.value)}
              className="w-full px-3 py-2 font-medium text-xs focus:outline-none focus:ring-2 transition-all cursor-pointer"
              style={inputStyle}
            >
              <option value="">All Tasks</option>
              <option value="recurring">🔄 Repeatable Only</option>
              <option value="non_recurring">One-off Only</option>
            </select>
          </div>

          {/* Date Picker 1: Start Date */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--theme-color-body-mid)" }}>
              Created After
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-1.5 font-medium text-xs focus:outline-none focus:ring-2 transition-all"
              style={inputStyle}
            />
          </div>

          {/* Date Picker 2: End Date */}
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide mb-1.5" style={{ color: "var(--theme-color-body-mid)" }}>
              Created Before
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-1.5 font-medium text-xs focus:outline-none focus:ring-2 transition-all"
              style={inputStyle}
            />
          </div>
        </div>
      </div>

      {/* Sorting helper for mobile */}
      <div
        className="lg:hidden border rounded-2xl shadow-sm p-4 mb-4 flex justify-between items-center text-xs"
        style={{
          backgroundColor: "var(--theme-card-bg)",
          borderColor: "var(--theme-color-mute)",
        }}
      >
        <span className="font-bold uppercase tracking-wider" style={{ color: "var(--theme-color-body-mid)" }}>
          Sort Parameters
        </span>
        <div className="flex gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-2.5 py-1 font-bold focus:outline-none"
            style={inputStyle}
          >
            <option value="taskNumber"># Task Num</option>
            <option value="dueDate">Due Date</option>
            <option value="createdAt">Created Date</option>
            <option value="priority">Priority</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="font-bold px-2 py-1 rounded"
            style={{
              backgroundColor: "var(--theme-color-canvas-soft)",
              color: "var(--theme-color-primary)",
            }}
          >
            {sortOrder === "asc" ? "Asc" : "Desc"}
          </button>
        </div>
      </div>

      {/* Table Content */}
      {loading ? (
        /* Full table skeleton loaders */
        <div
          className="rounded-2xl border shadow-sm overflow-hidden animate-pulse"
          style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}
        >
          <table className="w-full text-left border-collapse">
            <thead>
              <tr style={{ backgroundColor: "var(--theme-color-canvas-soft)", borderBottom: "1px solid var(--theme-color-mute)" }}>
                {[16, 48, 24, 24, 16, 16, 24, 24].map((w, i) => (
                  <th key={i} className="p-4"><div className={`h-4 bg-slate-200 rounded w-${w}`}></div></th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(6)].map((_, idx) => (
                <tr key={idx} style={{ borderBottom: "1px solid var(--theme-color-canvas-soft)" }}>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-10"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-72"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-24"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-28"></div></td>
                  <td className="p-4"><div className="h-6 bg-slate-200 rounded-full w-14"></div></td>
                  <td className="p-4"><div className="h-6 bg-slate-200 rounded-full w-16"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-20"></div></td>
                  <td className="p-4"><div className="h-4 bg-slate-200 rounded w-20"></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : sortedTasks.length === 0 ? (
        /* Empty State */
        <div
          className="rounded-2xl border shadow-sm py-16 px-4 text-center max-w-xl mx-auto"
          style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
            style={{ backgroundColor: "var(--theme-color-canvas-soft)", color: "var(--theme-color-body-mid)" }}
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              ></path>
            </svg>
          </div>
          <h3 className="text-lg font-bold m-0" style={{ color: "var(--theme-color-ink)" }}>
            No Matching Active Tasks
          </h3>
          <p className="mt-2 text-sm leading-relaxed max-w-sm mx-auto" style={{ color: "var(--theme-color-body)" }}>
            No active non-closed tasks were found matching the selected state, priority, assignee, or date range parameters.
          </p>
        </div>
      ) : (
        <div>
          {/* Desktop Table View */}
          <div
            className="hidden lg:block rounded-2xl border shadow-sm overflow-hidden"
            style={{ backgroundColor: "var(--theme-card-bg)", borderColor: "var(--theme-color-mute)" }}
          >
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-left border-collapse">
                <thead
                  className="sticky top-0 z-10"
                  style={{
                    backgroundColor: "var(--theme-color-canvas-soft)",
                    borderBottom: "1px solid var(--theme-color-mute)",
                  }}
                >
                  <tr className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--theme-color-body-mid)" }}>
                    <th
                      className="p-4 pl-6 cursor-pointer hover:bg-slate-100 select-none transition-colors w-28"
                      onClick={() => handleSortToggle("taskNumber")}
                    >
                      # Task {renderSortIndicator("taskNumber")}
                    </th>
                    <th className="p-4">Title</th>
                    <th className="p-4 w-44">Labels</th>
                    <th className="p-4">Assignee</th>
                    <th
                      className="p-4 cursor-pointer hover:bg-slate-100 select-none transition-colors w-32"
                      onClick={() => handleSortToggle("priority")}
                    >
                      Priority {renderSortIndicator("priority")}
                    </th>
                    <th className="p-4 w-32">State</th>
                    <th className="p-4 w-36">Repeat</th>
                    <th
                      className="p-4 cursor-pointer hover:bg-slate-100 select-none transition-colors w-40"
                      onClick={() => handleSortToggle("dueDate")}
                    >
                      Due Date {renderSortIndicator("dueDate")}
                    </th>
                    <th
                      className="p-4 pr-6 cursor-pointer hover:bg-slate-100 select-none transition-colors w-40"
                      onClick={() => handleSortToggle("createdAt")}
                    >
                      Created {renderSortIndicator("createdAt")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTasks.map((task) => (
                    <tr
                      key={task.id}
                      className="transition-colors"
                      style={{ borderBottom: "1px solid var(--theme-color-canvas-soft)" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--theme-color-canvas-soft)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <td className="p-4 pl-6 align-middle font-extrabold">
                        <Link
                          to={`/tasks/${task.taskNumber}`}
                          className="hover:underline no-underline"
                          style={{ color: "var(--theme-color-primary)" }}
                        >
                          {task.taskNumber}
                        </Link>
                      </td>
                      <td className="p-4 align-middle font-semibold max-w-sm truncate" style={{ color: "var(--theme-color-ink)" }}>
                        {task.title}
                      </td>
                      <td className="p-4 align-middle">
                        <div className="flex flex-wrap gap-1 max-w-[200px]">
                          {(task.labels || []).map((lName) => (
                            <LabelTag key={lName} name={lName} color={labelColorMap[lName]} />
                          ))}
                        </div>
                      </td>
                      <td className="p-4 align-middle font-medium text-sm" style={{ color: "var(--theme-color-body)" }}>
                        {getAssigneeName(task.assigneeUid)}
                      </td>
                      <td className="p-4 align-middle">
                        <span className={getPriorityBadgeClass(task.priority)}>
                          {task.priority || "Low"}
                        </span>
                      </td>
                      <td className="p-4 align-middle">
                        <span className={getStatusBadgeClass(task.status)}>
                          {task.status}
                        </span>
                      </td>
                      <td className="p-4 align-middle text-xs font-medium" style={{ color: "var(--theme-color-body)" }}>
                        {isRepeatableTask(task) ? (
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                              task.status === "Closed"
                                ? "text-gray-600 bg-gray-100"
                                : "text-indigo-700 bg-indigo-50"
                            }`}
                          >
                            <span>🔄</span>
                            <span>{describeRecurrence(task.recurrence)}</span>
                            {task.status === "Closed" && (
                              <span className="text-[10px] text-gray-500 font-normal ml-0.5">(Ended)</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="p-4 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                        {task.dueDate ? task.dueDate : "—"}
                      </td>
                      <td className="p-4 pr-6 align-middle text-sm font-medium" style={{ color: "var(--theme-color-body)" }}>
                        {formatFirestoreDate(task.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Stacked Card View */}
          <div className="lg:hidden space-y-4">
            {sortedTasks.map((task) => (
              <div
                key={task.id}
                className="rounded-2xl border shadow-sm p-5 space-y-3.5 transition-all"
                style={{
                  backgroundColor: "var(--theme-card-bg)",
                  borderColor: "var(--theme-color-mute)",
                  borderRadius: "var(--theme-card-radius)",
                }}
              >
                <div className="flex justify-between items-center">
                  <Link
                    to={`/tasks/${task.taskNumber}`}
                    className="text-xs font-extrabold hover:underline px-2.5 py-1 rounded"
                    style={{
                      color: "var(--theme-color-primary)",
                      backgroundColor: "var(--theme-color-canvas-soft)",
                    }}
                  >
                    #{task.taskNumber}
                  </Link>
                  <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                </div>

                <h3 className="text-base font-bold m-0 line-clamp-2" style={{ color: "var(--theme-color-ink)" }}>
                  {task.title}
                </h3>
                {task.labels && task.labels.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {task.labels.map((lName) => (
                      <LabelTag key={lName} name={lName} color={labelColorMap[lName]} />
                    ))}
                  </div>
                )}

                <div
                  className="pt-2 flex justify-between items-center text-xs font-semibold"
                  style={{ borderTop: "1px solid var(--theme-color-canvas-soft)" }}
                >
                  <div>
                    <span className="block font-medium" style={{ color: "var(--theme-color-body-mid)" }}>Assignee</span>
                    <span style={{ color: "var(--theme-color-ink)" }}>{getAssigneeName(task.assigneeUid)}</span>
                  </div>
                  <div>
                    <span className="block font-medium text-right" style={{ color: "var(--theme-color-body-mid)" }}>Priority</span>
                    <span className={getPriorityBadgeClass(task.priority)}>{task.priority || "Low"}</span>
                  </div>
                </div>

                <div className="pt-1.5 flex justify-between items-center text-xs font-medium" style={{ color: "var(--theme-color-body-mid)" }}>
                  <div className="flex flex-col">
                    <span>Created</span>
                    <span className="font-semibold" style={{ color: "var(--theme-color-body)" }}>
                      {formatFirestoreDate(task.createdAt)}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span>Due Date</span>
                    <span className="font-semibold" style={{ color: "var(--theme-color-body)" }}>
                      {task.dueDate ? task.dueDate : "—"}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
